import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { QueryParams } from './query-params';
import { Observable, Subject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import * as _ from 'lodash';
import { ActiveFilterService } from './../query/active-filter.service';
import { ConfigService } from './config.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { Body, SearchQuery } from '../../models/body';
import { SimpleMetricsHttpService } from '@/app/core/http/simple-metrics-http.service';
import InterfaceHttpService from '@/app/core/http/http-service.interface';

@Injectable({
  providedIn: 'root',
})
export class MetricsHttpService implements InterfaceHttpService {
  constructor(
    private metricsService: SimpleMetricsHttpService,
    private authenticationService: AuthenticationService,
    private filterService: ActiveFilterService,
    private config: ConfigService,
    private http: HttpClient,
    private router: Router
  ) {}

  public disableRoutingAbort(): MetricsHttpService {
    this.metricsService.disableRoutingAbort();
    return this;
  }

  public setCancelSubject(sub$: Subject<any>): InterfaceHttpService {
    this.metricsService.setCancelSubject(sub$);
    return this;
  }

  /**
   * Creates a new instance of MetricsHttpService with the same dependencies.
   * Each clone has its own shouldAbort flag, allowing isolated control.
   *
   * @returns A new MetricsHttpService instance
   *
   * @example
   * this.metricsHttpService.clone().disableRoutingAbort().get('/endpoint')
   */
  public clone(): MetricsHttpService {
    return new MetricsHttpService(
      this.metricsService.clone(), // Just clone the simple service
      this.authenticationService,
      this.filterService,
      this.config,
      this.http,
      this.router
    );
  }

  public delete = (...args: any[]): Observable<Record<any, any>> => {
    return this.metricsService.delete(...args);
  };

  public get = (...args: any[]): Observable<Record<any, any>> => {
    return this.metricsService.get(...args);
  };

  public patch = (...args: any[]): Observable<Record<any, any>> => {
    return this.metricsService.patch(...args);
  };

  public put = (...args: any[]): Observable<Record<any, any>> => {
    return this.metricsService.put(...args);
  };

  public post = (
    endpoint: string,
    content?: Body,
    overrideSearchQuery?: SearchQuery
  ): Observable<Record<any, any>> => {
    return this.metricsService.post(endpoint, content, overrideSearchQuery);
  };

  public postWithoutAuthentication = (
    endpoint: string,
    content?: Body,
    overrideSearchQuery?: SearchQuery
  ): Observable<Record<any, any>> => {
    return this.metricsService.postWithoutAuthentication(
      endpoint,
      content,
      overrideSearchQuery
    );
  };

  public getHeaders = (accessToken: string, clientId?: number): HttpHeaders => {
    return this.metricsService.getHeaders(accessToken, clientId);
  };

  public getWithoutAuthentication = (
    ...args: any[]
  ): Observable<Record<any, any>> => {
    return this.metricsService.getWithoutAuthentication(...args);
  };

  public getFile = (endpoint: string): Observable<Record<any, any>[]> => {
    return this.metricsService.getFile(endpoint);
  };

  /** Compose a GET with global filters, optionally excluding specified params */
  public getfx(except: string[], ...args: any[]): Observable<Record<any, any>> {
    return this.getInternal({ except, args });
  }

  /** Compose a GET with global filters */
  public getf(...args: any[]): Observable<Record<any, any>> {
    return this.getInternal({ args });
  }

  private getInternal(req: any): Observable<Record<any, any>> {
    const except = req.except;
    const args = req.args;
    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        let endpoint = args[0].indexOf('/') === 0 ? args[0] : '/' + args[0];
        args[1] = args[1] || {};
        let searchParams: QueryParams = null;
        const currentParams = new QueryParams();
        if (args.length > 1 && args[1]) {
          searchParams = args[1].search as QueryParams;
        }
        if (
          this.authenticationService &&
          this.authenticationService._userInfoView &&
          this.authenticationService._userInfoView.SelectedClient
        ) {
          let delimit = '?';
          if (endpoint.toLowerCase().indexOf('clientid=') === -1) {
            if (endpoint.indexOf('?') > -1) {
              delimit = '&';
            }
            endpoint =
              endpoint +
              delimit +
              'ClientId=' +
              this.authenticationService._userInfoView.SelectedClient.ClientID.toString();
          }
        }
        return this.filterService.filterParamsForGet.pipe(
          switchMap((f) => {
            searchParams = searchParams || new QueryParams();
            currentParams.appendAll(f);

            //replace rouse categories from global filter only if they're null in search params
            if (searchParams.has('RouseCategoryList')) {
              currentParams.delete('RouseCategoryList');
            }
            if (except) {
              _.forEach(except, (p) => {
                currentParams.delete(p);
              });
            }
            searchParams.appendAll(currentParams);
            if (token && token.AccessToken) {
              const headers = new HttpHeaders().set(
                'Authorization',
                `Bearer ${token.AccessToken}`
              );
              const params = this.toHttpParams(searchParams);
              return this.config.baseApiUrl.pipe(
                switchMap((apiUrl) => {
                  const url = apiUrl + endpoint;
                  return this.metricsService.get(url, { headers, params });
                })
              );
            }
            return new Observable<any>();
          })
        );
      })
    );
  }

  public toHttpParams = (params: QueryParams): HttpParams => {
    return this.metricsService.toHttpParams(params);
  };
}
